import Joi from 'joi';

// User roles enum (should match backend users.type)
export const UserRole = {
  USER: 'user',
  ADMIN: 'admin',
};

/** Numeric users.type values (splitshire-v2 user-type.constants) */
export const UserType = {
  SUPERADMIN: 0,
  APP_USER: 1,
  PROMPT_MANAGER: 2,
  ADS_MANAGER: 3,
};

export const USER_TYPE_LABELS = {
  [UserType.SUPERADMIN]: 'Super Admin',
  [UserType.APP_USER]: 'User',
  [UserType.PROMPT_MANAGER]: 'Prompt Manager',
  [UserType.ADS_MANAGER]: 'Ads Manager',
};

export const USER_TYPE_OPTIONS = [
  { value: UserType.APP_USER, label: USER_TYPE_LABELS[UserType.APP_USER] },
  { value: UserType.PROMPT_MANAGER, label: USER_TYPE_LABELS[UserType.PROMPT_MANAGER] },
  { value: UserType.ADS_MANAGER, label: USER_TYPE_LABELS[UserType.ADS_MANAGER] },
  { value: UserType.SUPERADMIN, label: USER_TYPE_LABELS[UserType.SUPERADMIN] },
];

// Create User Schema
export const createUserSchema = Joi.object({
  email: Joi.string().email().required().messages({
    'string.email': 'Please enter a valid email address',
    'string.empty': 'Email is required',
    'any.required': 'Email is required',
    'any.empty': 'Email is required',
  }),
  username: Joi.string().min(3).pattern(/^[a-zA-Z0-9_]+$/).required().messages({
    'string.min': 'Username must be at least 3 characters',
    'string.pattern.base': 'Username can only contain letters, numbers, and underscores',
    'string.empty': 'Username is required',
    'any.required': 'Username is required',
    'any.empty': 'Username is required',
  }),
  password: Joi.string().min(6).required().messages({
    'string.min': 'Password must be at least 6 characters',
    'string.empty': 'Password is required',
    'any.required': 'Password is required',
    'any.empty': 'Password is required',
  }),
  name: Joi.string().required().messages({
    'string.empty': 'Name is required',
    'any.required': 'Name is required',
    'any.empty': 'Name is required',
  }),
  tagline: Joi.string().max(255).optional().allow('', null).messages({
    'string.max': 'Tagline must be less than 255 characters',
  }),
  role: Joi.string().valid(...Object.values(UserRole)).optional().messages({
    'any.only': 'Please select a valid role',
  }),
  isActive: Joi.boolean().optional(),
});

// Update User Schema
export const updateUserSchema = Joi.object({
  email: Joi.string().email().optional().messages({
    'string.email': 'Please enter a valid email address',
  }),
  username: Joi.string().min(3).pattern(/^[a-zA-Z0-9_]+$/).optional().allow('', null).messages({
    'string.min': 'Username must be at least 3 characters',
    'string.pattern.base': 'Username can only contain letters, numbers, and underscores',
  }),
  password: Joi.string()
    .optional()
    .allow('', null)
    .custom((value, helpers) => {
      if (value && value.length > 0 && value.length < 6) {
        return helpers.error('string.min');
      }
      return value;
    })
    .messages({
      'string.min': 'Password must be at least 6 characters',
    }),
  name: Joi.string().optional(),
  tagline: Joi.string().max(255).optional().allow('', null).messages({
    'string.max': 'Tagline must be less than 255 characters',
  }),
  role: Joi.string().valid(...Object.values(UserRole)).optional().messages({
    'any.only': 'Please select a valid role',
  }),
  isActive: Joi.boolean().optional(),
  avatar: Joi.string().optional().allow('', null),
  phoneNumber: Joi.string()
    .pattern(/^\d{10}$/) // 10 digits only
    .optional()
    .allow('', null)
    .messages({
      'string.pattern.base': 'Please enter a valid 10-digit phone number',
      'string.base': 'Phone number must be a valid number',
    }),
  website: Joi.string()
    .uri({ scheme: ['http', 'https'] })
    .optional()
    .allow('', null)
    .messages({
      'string.uri': 'Please enter a valid website URL. It should start with http:// or https://',
      'string.uriCustomScheme': 'Please enter a valid website URL. It should start with http:// or https://',
      'string.base': 'Please enter a valid website URL',
      'any.only': 'Please enter a valid website URL',
    }),
  address: Joi.string().optional().allow('', null),
  city: Joi.string().optional().allow('', null),
  postcode: Joi.string().optional().allow('', null),
  dateOfBirth: Joi.any().optional().allow(null, '').messages({
    'any.only': 'Please enter a valid date',
  }),
});

